import "./quadrant.css";
import React from "react";
import { percentToDecimal, isNumber } from "../../../../util/numbers";
import { useDisclosure } from "@mantine/hooks";
import { Text, Popover, Box } from "@mantine/core";

const _circleWidth = 125;
const _circleHeight = 125;

const QuadrantDescriptionWrapper = ({
  skillDescription,
  style,
  variant,
  hide,
  position,
  children,
}) => {
  if (!skillDescription || hide) {
    return <Box style={style}>{children}</Box>;
  }
  const [opened, { open, close }] = useDisclosure(false);
  const variantKeys = Object.keys(skillDescription.variants || {});

  return (
    <Popover
      width="auto"
      position={position}
      shadow="md"
      opened={opened}
      withinPortal
      withArrow
      offset={0}
    >
      <Popover.Target>
        <Box onMouseEnter={open} onMouseLeave={close} style={style}>
          {children}
        </Box>
      </Popover.Target>

      <Popover.Dropdown style={{ maxWidth: 600 }}>
        <Text size="sm" mb="sm">
          {skillDescription.description}
        </Text>

        {variantKeys.length > 0 && (
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {variantKeys.map((key) => {
              const isActive = key === variant;
              return (
                <li
                  key={key}
                  style={{ opacity: isActive ? 1 : 0.5, marginBottom: 4 }}
                >
                  <Text
                    size="sm"
                    component="span"
                    fw="bold"
                    style={{ textTransform: "capitalize" }}
                  >
                    {key}:&nbsp;
                  </Text>
                  <Text size="sm" component="span">
                    {skillDescription.variants[key]}
                  </Text>
                </li>
              );
            })}
          </ul>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default function QuadrantBubble(props) {
  const {
    id,
    dimensions,
    graphObj,
    children,
    displayName,
    backgroundColor,
    disabled,
    inUse,
    top,
    left,
    textColor,
    onClick,
    overrideCircleWidth,
    overrideCircleHeight,
    skillDescription,
    variant,
  } = props;

  const circleWidth = overrideCircleWidth || _circleWidth;
  const circleHeight = overrideCircleHeight || _circleHeight;

  function calcCenterX(leftPercentage) {
    return percentToDecimal(leftPercentage) * dimensions.width;
  }

  function calcCenterY(topPercentage) {
    return percentToDecimal(topPercentage) * dimensions.height;
  }

  const centerX = calcCenterX(left);
  const centerY = calcCenterY(top);

  // choose popover position based on vertical placement
  const popoverPosition = centerY < dimensions.height / 2 ? "left" : "top";

  const positionStyle = {
    position: "absolute",
    top: centerY - circleHeight / 2,
    left: centerX - circleWidth / 2,
  };
  const buttonStyle = {
    backgroundColor: inUse || !disabled ? backgroundColor : "lightgrey",
    color: !disabled ? textColor : "#3A1E19",
    width: circleWidth,
    height: circleHeight,
    borderRadius: "50%",
  };

  const childQuadrants = graphObj.quadrants.filter((q) =>
    children.includes(q.id),
  );

  if (!isNumber(centerX) || !isNumber(centerY)) {
    return null;
  }

  return (
    <>
      <QuadrantDescriptionWrapper
        style={positionStyle}
        skillDescription={skillDescription}
        variant={variant}
        hide={disabled}
        position={popoverPosition}
      >
        <button
          key={`${id}-circle`}
          className={`quadrant-circle${
            disabled ? " quadrant-dimmed" : " hover-shadow"
          }${inUse ? " in-use-ring" : ""}`}
          style={buttonStyle}
          onClick={onClick}
          disabled={disabled}
        >
          {displayName}
        </button>
      </QuadrantDescriptionWrapper>

      <svg
        width={dimensions.width}
        height={dimensions.height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        {childQuadrants.map((child) => {
          const childInUse = child.inUse;
          const childCenterX = calcCenterX(child.left);
          const childCenterY = calcCenterY(child.top);

          return (
            <line
              key={`${id}-${child.id}-line`}
              className={`line${inUse || childInUse ? " line-glow" : ""}${
                inUse && childInUse ? " line-gold" : ""
              }`}
              x1={centerX}
              y1={centerY}
              x2={childCenterX}
              y2={childCenterY}
              stroke="black"
            />
          );
        })}
      </svg>
    </>
  );
}
